import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, Plus, Trash2, Edit3, RefreshCw, ArrowLeft, ShieldAlert, Save, Ticket, ListChecks, Activity, CalendarDays, Zap, Vault, TrendingUp, ArrowUpCircle, CheckCircle2, Copy, Loader2, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtUsd(cents) { return cents == null ? "—" : `$${(cents / 100).toFixed(2)}`; }
function fmtDate(iso) { try { return new Date(iso).toLocaleString("pt-BR", { hour12: false }); } catch { return iso; } }

/** Admin console — gated by a session-local admin secret input. */
export default function Admin() {
  const nav = useNavigate();
  const [secret, setSecret] = useState(() => sessionStorage.getItem("ufo_admin_secret") || "");
  const [authed, setAuthed] = useState(false);
  // Deep-link support: ?tab=payouts opens the payouts tab directly (from /painel-ufo-2077 redirect)
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") || "payouts";
  });
  const [config, setConfig] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [ticketStats, setTicketStats] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  // Payouts + Cofre state
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [paidPayouts, setPaidPayouts] = useState([]);
  const [cancelledPayouts, setCancelledPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [house, setHouse] = useState(null);

  const H = () => ({ "x-admin-secret": secret, "Content-Type": "application/json" });

  const check = useCallback(async (s) => {
    try {
      const r = await fetch(`${API}/api/admin/config`, { headers: { "x-admin-secret": s || secret } });
      if (r.ok) {
        setAuthed(true);
        sessionStorage.setItem("ufo_admin_secret", s || secret);
        setConfig(await r.json());
      } else { setAuthed(false); setErr("Secret inválido"); }
    } catch (e) { setErr(e.message); }
  }, [secret]);

  useEffect(() => { if (secret) check(secret); }, []);

  const loadAll = useCallback(async () => {
    if (!authed) return;
    try {
      const [c, r, l, t, e, pp, pd, pc, s, h] = await Promise.all([
        fetch(`${API}/api/admin/config`, { headers: H() }),
        fetch(`${API}/api/admin/free-rooms`, { headers: H() }),
        fetch(`${API}/api/admin/logs?limit=50`, { headers: H() }),
        fetch(`${API}/api/admin/tickets/summary`, { headers: H() }),
        fetch(`${API}/api/admin/events`, { headers: H() }),
        fetch(`${API}/api/admin/payouts/pending`, { headers: H() }),
        fetch(`${API}/api/admin/payouts/paid?limit=30`, { headers: H() }),
        fetch(`${API}/api/admin/payouts/cancelled?limit=30`, { headers: H() }),
        fetch(`${API}/api/admin/stats`, { headers: H() }),
        fetch(`${API}/api/admin/house`, { headers: H() }),
      ]);
      if (c.ok) setConfig(await c.json());
      if (r.ok) setRooms((await r.json()).rooms || []);
      if (l.ok) setLogs((await l.json()).logs || []);
      if (t.ok) setTicketStats((await t.json()).totals || {});
      if (e.ok) setEvents((await e.json()).events || []);
      if (pp.ok) setPendingPayouts((await pp.json()).payouts || []);
      if (pd.ok) setPaidPayouts((await pd.json()).payouts || []);
      if (pc.ok) setCancelledPayouts((await pc.json()).payouts || []);
      if (s.ok) setStats(await s.json());
      if (h.ok) setHouse(await h.json());
    } catch (e) { setErr(e.message); }
  }, [authed]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveConfig = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/admin/config`, {
        method: "PUT", headers: H(),
        body: JSON.stringify({
          ticket_grant_cents: config.ticket_grant_cents,
          ticket_validity_days: config.ticket_validity_days,
        }),
      });
      if (!r.ok) throw new Error("Falha ao salvar");
      setConfig(await r.json()); loadAll();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const createRoom = async (form) => {
    setErr(null);
    const r = await fetch(`${API}/api/admin/free-rooms`, { method: "POST", headers: H(), body: JSON.stringify(form) });
    if (!r.ok) setErr((await r.json()).detail || "Falha ao criar"); else loadAll();
  };
  const patchRoom = async (id, updates) => {
    await fetch(`${API}/api/admin/free-rooms/${id}`, { method: "PATCH", headers: H(), body: JSON.stringify(updates) });
    loadAll();
  };
  const delRoom = async (id) => {
    if (!window.confirm("Excluir esta sala?")) return;
    const r = await fetch(`${API}/api/admin/free-rooms/${id}`, { method: "DELETE", headers: H() });
    if (!r.ok) setErr((await r.json()).detail || "Falha ao excluir"); else loadAll();
  };
  const generateWeekly = async () => {
    setErr(null);
    const r = await fetch(`${API}/api/admin/free-rooms/generate-weekly`, { method: "POST", headers: H() });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.detail || "Falha"); return; }
    alert(`Sala semanal criada: ${d.label} · Prêmio $${(d.prize_pool_cents / 100).toFixed(2)}`);
    loadAll();
  };
  const createEvent = async (form) => {
    const r = await fetch(`${API}/api/admin/events`, { method: "POST", headers: H(), body: JSON.stringify(form) });
    if (!r.ok) setErr((await r.json()).detail || "Falha ao criar evento"); else loadAll();
  };
  const patchEvent = async (id, updates) => {
    await fetch(`${API}/api/admin/events/${id}`, { method: "PATCH", headers: H(), body: JSON.stringify(updates) });
    loadAll();
  };
  const delEvent = async (id) => {
    if (!window.confirm("Excluir este evento?")) return;
    await fetch(`${API}/api/admin/events/${id}`, { method: "DELETE", headers: H() });
    loadAll();
  };

  const createTournament = async (form) => {
    setErr(null);
    const r = await fetch(`${API}/api/admin/tournaments`, { method: "POST", headers: H(), body: JSON.stringify(form) });
    if (!r.ok) setErr((await r.json()).detail || "Falha ao criar torneio"); else loadAll();
  };

  const markPayoutPaid = async (payoutId, txHash, note) => {
    setErr(null);
    const r = await fetch(`${API}/api/admin/payouts/${payoutId}/paid`, {
      method: "POST", headers: H(), body: JSON.stringify({ tx_hash: txHash, note }),
    });
    if (!r.ok) { setErr((await r.json()).detail || "Falha ao marcar pago"); return false; }
    loadAll();
    return true;
  };
  const cancelPayout = async (payoutId, reason) => {
    setErr(null);
    const r = await fetch(`${API}/api/admin/payouts/${payoutId}/cancel`, {
      method: "POST", headers: H(), body: JSON.stringify({ reason }),
    });
    if (!r.ok) { setErr((await r.json()).detail || "Falha ao cancelar"); return false; }
    loadAll();
    return true;
  };
  const houseWithdraw = async (amountCents, note) => {
    setErr(null);
    const r = await fetch(`${API}/api/admin/house/withdraw`, {
      method: "POST", headers: H(), body: JSON.stringify({ amount_cents: amountCents, note }),
    });
    if (!r.ok) { setErr((await r.json()).detail || "Falha"); return false; }
    loadAll();
    return true;
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-black grid place-items-center text-white p-6">
        <div className="max-w-md w-full space-y-4 border border-primary/40 bg-black/70 backdrop-blur rounded-sm p-6">
          <div className="flex items-center gap-2 text-primary">
            <ShieldAlert className="h-5 w-5" />
            <p className="font-mono text-[10px] tracking-[0.3em]">// ADMIN CONSOLE</p>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Área restrita</h1>
          <input type="password" data-testid="admin-secret-input"
            value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Admin secret" autoFocus
            className="w-full bg-black/60 border border-primary/40 rounded-sm px-3 py-2 font-mono text-xs" />
          {err && <p className="font-mono text-[10px] text-red-400">{err}</p>}
          <button onClick={() => check()} data-testid="admin-secret-submit"
            className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest py-2">ENTRAR</button>
          <button onClick={() => nav("/")} className="w-full text-muted-foreground font-mono text-[10px] tracking-widest py-2 hover:text-primary">← voltar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_#050a18_0%,_#020408_60%,_#000_100%)] text-white pb-24">
      <div className="sticky top-0 z-20 border-b border-primary/20 bg-black/70 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button onClick={() => nav("/")} className="inline-flex items-center gap-1.5 rounded-sm border border-primary/30 px-3 py-1.5 hover:bg-primary/10 text-primary font-mono text-[10px] tracking-widest">
            <ArrowLeft className="h-3 w-3" /> VOLTAR
          </button>
          <div className="font-mono text-[9px] tracking-[0.3em] text-muted-foreground">// ADMIN · UFO CONSOLE</div>
          <button onClick={loadAll} data-testid="admin-refresh" className="inline-flex items-center gap-1.5 rounded-sm border border-primary/30 px-3 py-1.5 hover:bg-primary/10 text-primary font-mono text-[10px] tracking-widest">
            <RefreshCw className="h-3 w-3" /> ATUALIZAR
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pt-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Kpi label="Cofre" value={fmtUsd(house?.balance_cents ?? 0)} icon={Vault} accent="emerald" />
          <Kpi label="Saques Pend." value={pendingPayouts.length} icon={ArrowUpCircle} accent="amber" />
          <Kpi label="Salas Grátis" value={rooms.filter(r => r.entry_type === "ticket").length} icon={ListChecks} />
          <Kpi label="Eventos" value={events.length} icon={CalendarDays} />
          <Kpi label="Tickets Ativos" value={ticketStats?.active ?? 0} icon={Ticket} />
          <Kpi label="Logs" value={logs.length} icon={Activity} />
        </div>

        <div className="flex gap-2 border-b border-primary/20 overflow-x-auto">
          {[
            { id: "payouts",    label: "PAYOUTS · COFRE" },
            { id: "coupons",    label: "CUPONS BÔNUS" },
            { id: "free-rooms", label: "SALAS GRÁTIS" },
            { id: "tournaments", label: "TORNEIOS" },
            { id: "events",     label: "EVENTOS" },
            { id: "config",     label: "CONFIGURAÇÃO" },
            { id: "logs",       label: "LOGS" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-3 py-2 font-mono text-[10px] tracking-widest whitespace-nowrap ${tab === t.id ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {err && <div className="rounded-sm border border-red-400/40 bg-red-500/10 p-2 font-mono text-xs text-red-300">{err}</div>}

        {tab === "payouts" && (
          <PayoutsPanel
            house={house}
            stats={stats}
            pending={pendingPayouts}
            paid={paidPayouts}
            cancelled={cancelledPayouts}
            onMarkPaid={markPayoutPaid}
            onCancel={cancelPayout}
            onHouseWithdraw={houseWithdraw}
          />
        )}

        {tab === "coupons" && <CouponsPanel H={H} setErr={setErr} />}

        {tab === "free-rooms" && (
          <div className="space-y-3">
            <button onClick={generateWeekly} data-testid="admin-generate-weekly"
              className="inline-flex items-center gap-2 rounded-sm border border-amber-300/60 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 font-mono text-[10px] tracking-widest px-3 py-2">
              <Zap className="h-3.5 w-3.5" /> GERAR SALA SEMANAL AUTOMÁTICA
            </button>
            <FreeRoomsPanel rooms={rooms.filter(r => r.entry_type === "ticket")} onCreate={createRoom} onPatch={patchRoom} onDelete={delRoom} />
          </div>
        )}
        {tab === "tournaments" && (
          <TournamentPanel rooms={rooms.filter(r => r.entry_type === "tournament")} onCreate={createTournament} onDelete={delRoom} />
        )}
        {tab === "events" && (
          <EventsPanel events={events} onCreate={createEvent} onPatch={patchEvent} onDelete={delEvent} />
        )}
        {tab === "config" && config && (
          <ConfigPanel config={config} setConfig={setConfig} save={saveConfig} saving={saving} />
        )}
        {tab === "logs" && <LogsPanel logs={logs} />}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent = "primary" }) {
  const borders = {
    primary: "border-primary/20 text-primary",
    emerald: "border-emerald-400/40 text-emerald-300",
    amber:   "border-amber-300/40 text-amber-300",
  };
  const bg = {
    primary: "bg-primary/10",
    emerald: "bg-emerald-500/10",
    amber:   "bg-amber-500/10",
  };
  const b = borders[accent] || borders.primary;
  const bgc = bg[accent] || bg.primary;
  return (
    <div className={`rounded-sm border bg-black/50 p-3 flex items-center gap-3 ${b}`}>
      <div className={`h-9 w-9 rounded-sm border ${b} ${bgc} grid place-items-center`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="font-mono text-[9px] tracking-widest text-muted-foreground">{label}</p>
        <p className="font-display text-2xl font-black tracking-tighter">{value}</p>
      </div>
    </div>
  );
}

function ConfigPanel({ config, setConfig, save, saving }) {
  const preview = (() => {
    const c = Math.max(1, Number(config.ticket_grant_cents) || 200);
    return [200, 500, 1000, 1500].map((cents) => ({ cents, tickets: Math.max(1, Math.floor(cents / c)) }));
  })();
  return (
    <div className="space-y-4 max-w-lg">
      <div className="rounded-sm border border-primary/30 bg-black/50 p-4 space-y-3">
        <div className="flex items-center gap-2 text-primary"><Settings className="h-4 w-4" /><p className="font-mono text-[9px] tracking-widest">// TICKETS</p></div>
        <NumberField label="CENTS POR TICKET (grant)" value={config.ticket_grant_cents}
          onChange={(v) => setConfig((c) => ({ ...c, ticket_grant_cents: v }))} testid="admin-config-grant-cents"
          hint="Ex: 200 = 1 ticket a cada $2" />
        <NumberField label="VALIDADE DO TICKET (dias)" value={config.ticket_validity_days}
          onChange={(v) => setConfig((c) => ({ ...c, ticket_validity_days: v }))} testid="admin-config-validity" />
        <div className="border-t border-primary/10 pt-3">
          <p className="font-mono text-[9px] tracking-widest text-muted-foreground mb-2">PRÉVIA</p>
          <div className="grid grid-cols-2 gap-2 font-mono text-[11px]">
            {preview.map((p) => (
              <div key={p.cents} className="flex justify-between border border-primary/10 rounded-sm px-2 py-1">
                <span className="text-muted-foreground">Entrada ${(p.cents / 100).toFixed(2)}</span>
                <span className="text-amber-200 font-bold">→ {p.tickets} 🎟</span>
              </div>
            ))}
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-config-save"
          className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest py-2 disabled:opacity-50">
          <Save className="h-3.5 w-3.5" /> {saving ? "SALVANDO..." : "SALVAR CONFIGURAÇÃO"}
        </button>
      </div>
    </div>
  );
}

function FreeRoomsPanel({ rooms, onCreate, onPatch, onDelete }) {
  const [form, setForm] = useState({
    label: "TORNEIO GRÁTIS", prize_pool_cents: 5000, entry_ticket_count: 1,
    capacity: 30, min_players: 15, starts_in_minutes: 60, duration_minutes: 60,
  });
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-sm border border-primary/30 bg-black/50 p-4 space-y-3">
        <div className="flex items-center gap-2 text-primary"><Plus className="h-4 w-4" /><p className="font-mono text-[9px] tracking-widest">// CRIAR SALA GRÁTIS</p></div>
        <TextField label="LABEL" value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="admin-fr-label" />
        <NumberField label="PRÊMIO (CENTS)" value={form.prize_pool_cents} onChange={(v) => setForm({ ...form, prize_pool_cents: v })} hint="Ex: 5000 = $50" testid="admin-fr-prize" />
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="TICKETS/ENTRADA" value={form.entry_ticket_count} onChange={(v) => setForm({ ...form, entry_ticket_count: v })} testid="admin-fr-ticket-count" />
          <NumberField label="CAPACIDADE" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} testid="admin-fr-capacity" />
          <NumberField label="MÍNIMO" value={form.min_players} onChange={(v) => setForm({ ...form, min_players: v })} testid="admin-fr-min" />
          <NumberField label="INÍCIO EM (MIN)" value={form.starts_in_minutes} onChange={(v) => setForm({ ...form, starts_in_minutes: v })} hint="0 = começa agora" testid="admin-fr-start" />
          <NumberField label="DURAÇÃO (MIN)" value={form.duration_minutes} onChange={(v) => setForm({ ...form, duration_minutes: v })} testid="admin-fr-duration" />
        </div>
        <button onClick={() => onCreate(form)} data-testid="admin-fr-create"
          className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-amber-500 text-black hover:bg-amber-400 font-mono text-xs tracking-widest py-2">
          <Plus className="h-3.5 w-3.5" /> CRIAR SALA
        </button>
      </div>
      <div className="space-y-2">
        <p className="font-mono text-[9px] tracking-widest text-muted-foreground">// EXISTENTES ({rooms.length})</p>
        {rooms.length === 0 && <p className="text-xs text-muted-foreground italic">Nenhuma sala grátis ainda.</p>}
        {rooms.map((r) => (
          <div key={r.room_id} data-testid={`admin-fr-row-${r.room_id}`} className="rounded-sm border border-primary/20 bg-black/50 p-3 space-y-2">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <p className="font-mono text-[9px] tracking-widest text-amber-200">{r.status.toUpperCase()} · {r.entry_ticket_count || 1} 🎟</p>
                <h4 className="font-display font-black text-lg tracking-tighter truncate">{r.label}</h4>
                <p className="font-mono text-[10px] text-muted-foreground">
                  Prêmio ${((r.prize_pool_cents || 0) / 100).toFixed(2)} · {r.player_count}/{r.capacity} · min {r.min_players}
                </p>
              </div>
              <button onClick={() => onDelete(r.room_id)} data-testid={`admin-fr-del-${r.room_id}`}
                className="p-1.5 rounded-sm border border-red-400/40 text-red-300 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => {
                const p = window.prompt("Novo prêmio em centavos (ex: 5000):", r.prize_pool_cents);
                if (p != null && !isNaN(+p)) onPatch(r.room_id, { prize_pool_cents: +p });
              }} className="font-mono text-[10px] tracking-widest border border-primary/30 rounded-sm py-1 hover:bg-primary/10">
                <Edit3 className="h-3 w-3 inline mr-1" /> PRÊMIO
              </button>
              <button onClick={() => onPatch(r.room_id, { status: r.status === "open" ? "cancelled" : "open" })}
                className="font-mono text-[10px] tracking-widest border border-primary/30 rounded-sm py-1 hover:bg-primary/10">
                {r.status === "open" ? "DESATIVAR" : "REATIVAR"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TournamentPanel({ rooms, onCreate, onDelete }) {
  const [form, setForm] = useState({
    label: "TORNEIO DIÁRIO", capacity: 50, min_players: 10,
    starts_in_minutes: 60, duration_minutes: 60,
  });
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-sm border border-cyan-400/40 bg-cyan-500/5 p-4 space-y-3">
        <div className="flex items-center gap-2 text-cyan-200">
          <Zap className="h-4 w-4" />
          <p className="font-mono text-[9px] tracking-widest">// CRIAR TORNEIO GRÁTIS</p>
        </div>
        <p className="text-xs text-muted-foreground">Entrada 100% grátis. Top 10 ganha tickets: 5/3/3/2/2/1/1/1/1/1</p>
        <TextField label="LABEL" value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="admin-tr-label" />
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="CAPACIDADE" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} testid="admin-tr-capacity" />
          <NumberField label="MÍNIMO" value={form.min_players} onChange={(v) => setForm({ ...form, min_players: v })} testid="admin-tr-min" />
          <NumberField label="INÍCIO EM (MIN)" value={form.starts_in_minutes} onChange={(v) => setForm({ ...form, starts_in_minutes: v })} hint="0 = começa agora" testid="admin-tr-start" />
          <NumberField label="DURAÇÃO (MIN)" value={form.duration_minutes} onChange={(v) => setForm({ ...form, duration_minutes: v })} testid="admin-tr-duration" />
        </div>
        <button onClick={() => onCreate(form)} data-testid="admin-tr-create"
          className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 font-mono text-xs tracking-widest py-2">
          <Plus className="h-3.5 w-3.5" /> CRIAR TORNEIO
        </button>
      </div>
      <div className="space-y-2">
        <p className="font-mono text-[9px] tracking-widest text-muted-foreground">// TORNEIOS EXISTENTES ({rooms.length})</p>
        {rooms.length === 0 && <p className="text-xs text-muted-foreground italic">Nenhum torneio ainda.</p>}
        {rooms.map((r) => (
          <div key={r.room_id} data-testid={`admin-tr-row-${r.room_id}`} className="rounded-sm border border-cyan-400/30 bg-black/50 p-3">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <p className="font-mono text-[9px] tracking-widest text-cyan-200">{r.status.toUpperCase()} · TORNEIO</p>
                <h4 className="font-display font-black text-lg tracking-tighter truncate">{r.label}</h4>
                <p className="font-mono text-[10px] text-muted-foreground">
                  {r.player_count}/{r.capacity} · min {r.min_players} · Prêmio: 20 🎟 (top 10)
                </p>
                <p className="font-mono text-[9px] text-muted-foreground mt-1">
                  {new Date(r.scheduled_start).toLocaleString("pt-BR")}
                </p>
              </div>
              <button onClick={() => onDelete(r.room_id)} data-testid={`admin-tr-del-${r.room_id}`}
                className="p-1.5 rounded-sm border border-red-400/40 text-red-300 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsPanel({ events, onCreate, onPatch, onDelete }) {
  const now = new Date();
  const initStart = new Date(now.getTime() + 3600_000).toISOString().slice(0, 16);
  const initEnd = new Date(now.getTime() + 3 * 86400_000).toISOString().slice(0, 16);
  const [form, setForm] = useState({
    name: "DOBRO DE TICKETS", description: "Ganhe o dobro em toda sala paga",
    banner_url: "", event_type: "ticket_multiplier",
    starts_at: initStart, ends_at: initEnd,
    rules: "Válido em salas pagas somente.", rewards: "2× tickets por entrada",
    config_multiplier: 2, status: "active",
  });
  const submit = () => onCreate({
    ...form, starts_at: new Date(form.starts_at).toISOString(),
    ends_at: new Date(form.ends_at).toISOString(),
    config: { multiplier: Number(form.config_multiplier) || 1 },
  });
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-sm border border-primary/30 bg-black/50 p-4 space-y-3">
        <div className="flex items-center gap-2 text-primary"><CalendarDays className="h-4 w-4" /><p className="font-mono text-[9px] tracking-widest">// CRIAR EVENTO</p></div>
        <TextField label="NOME" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-ev-name" />
        <label className="block">
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground">DESCRIÇÃO</span>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1 w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-xs h-16" />
        </label>
        <TextField label="BANNER URL (opcional)" value={form.banner_url} onChange={(v) => setForm({ ...form, banner_url: v })} testid="admin-ev-banner" />
        <label className="block">
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground">TIPO</span>
          <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}
            data-testid="admin-ev-type"
            className="mt-1 w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-xs">
            <option value="ticket_multiplier">Multiplicador de Tickets</option>
            <option value="reward_multiplier">Multiplicador de Prêmios</option>
            <option value="cashback">Cashback</option>
            <option value="free_entry">Entrada Grátis</option>
            <option value="custom">Personalizado</option>
          </select>
        </label>
        <NumberField label="MULTIPLICADOR (config)" value={form.config_multiplier}
          onChange={(v) => setForm({ ...form, config_multiplier: v })} testid="admin-ev-multi" />
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-muted-foreground">INÍCIO</span>
            <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
              className="mt-1 w-full bg-black/60 border border-primary/30 rounded-sm px-2 py-2 font-mono text-xs" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-muted-foreground">TÉRMINO</span>
            <input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              className="mt-1 w-full bg-black/60 border border-primary/30 rounded-sm px-2 py-2 font-mono text-xs" />
          </label>
        </div>
        <TextField label="REGRAS" value={form.rules} onChange={(v) => setForm({ ...form, rules: v })} testid="admin-ev-rules" />
        <TextField label="RECOMPENSAS" value={form.rewards} onChange={(v) => setForm({ ...form, rewards: v })} testid="admin-ev-rewards" />
        <button onClick={submit} data-testid="admin-ev-create"
          className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 font-mono text-xs tracking-widest py-2">
          <Plus className="h-3.5 w-3.5" /> CRIAR EVENTO
        </button>
      </div>
      <div className="space-y-2">
        <p className="font-mono text-[9px] tracking-widest text-muted-foreground">// EVENTOS ({events.length})</p>
        {events.length === 0 && <p className="text-xs text-muted-foreground italic">Nenhum evento ainda.</p>}
        {events.map((e) => (
          <div key={e.event_id} data-testid={`admin-ev-row-${e.event_id}`} className="rounded-sm border border-primary/20 bg-black/50 p-3 space-y-2">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <p className={`font-mono text-[9px] tracking-widest ${e.status === "active" ? "text-emerald-300" : "text-muted-foreground"}`}>
                  {e.status.toUpperCase()} · {e.event_type}
                </p>
                <h4 className="font-display font-black text-lg tracking-tighter truncate">{e.name}</h4>
                <p className="font-mono text-[10px] text-muted-foreground line-clamp-2">{e.description}</p>
                <p className="font-mono text-[9px] text-muted-foreground mt-1">
                  {new Date(e.starts_at).toLocaleString("pt-BR")} → {new Date(e.ends_at).toLocaleString("pt-BR")}
                </p>
              </div>
              <button onClick={() => onDelete(e.event_id)} data-testid={`admin-ev-del-${e.event_id}`}
                className="p-1.5 rounded-sm border border-red-400/40 text-red-300 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <button onClick={() => onPatch(e.event_id, { status: e.status === "active" ? "inactive" : "active" })}
              className="w-full font-mono text-[10px] tracking-widest border border-primary/30 rounded-sm py-1 hover:bg-primary/10">
              {e.status === "active" ? "DESATIVAR" : "ATIVAR"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LogsPanel({ logs }) {
  return (
    <div className="rounded-sm border border-primary/20 bg-black/50 p-3 max-h-[70vh] overflow-y-auto space-y-1">
      {logs.length === 0 && <p className="text-xs text-muted-foreground italic">Sem logs ainda.</p>}
      {logs.map((l) => (
        <div key={l.log_id} className="border-b border-primary/10 py-2 font-mono text-[10px]">
          <div className="flex justify-between items-baseline">
            <span className="text-amber-200 font-bold">{l.action}</span>
            <span className="text-muted-foreground">{new Date(l.created_at).toLocaleString("pt-BR")}</span>
          </div>
          <pre className="text-muted-foreground text-[9px] mt-1 whitespace-pre-wrap break-all">
            {JSON.stringify(l.details, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  );
}

function CancelPayoutModal({ payout, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!payout) return null;
  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try { await onSubmit(reason.trim()); }
    finally { setBusy(false); }
  };
  const gross = payout.gross_cents || payout.amount_cents;
  return (
    <div data-testid="cancel-payout-modal"
         className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-sm border border-red-400/50 bg-black p-5 space-y-4">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-red-300">// CANCELAR SAQUE</p>
          <p className="text-white font-display font-black text-lg tracking-tighter mt-1">
            {payout.display_name} — {fmtUsd(gross)}
          </p>
          <p className="font-mono text-[10px] text-muted-foreground mt-1">
            O valor bloqueado voltará pra carteira de Prêmios do piloto e a taxa será estornada da Casa.
          </p>
        </div>
        <label className="block">
          <span className="font-mono text-[9px] tracking-widest text-muted-foreground">MOTIVO (obrigatório)</span>
          <textarea
            data-testid="cancel-reason-input"
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: endereço inválido, jogador pediu cancelamento, análise antifraude..."
            className="w-full mt-1 bg-black/70 border border-red-400/30 rounded-sm px-3 py-2 font-mono text-xs text-red-100 min-h-[70px]"
          />
        </label>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} data-testid="cancel-modal-close"
                  className="rounded-sm border border-primary/40 px-3 py-2 font-mono tracking-widest text-[10px] text-primary hover:bg-primary/10">
            VOLTAR
          </button>
          <button onClick={submit} disabled={busy || reason.trim().length < 3}
                  data-testid="cancel-modal-confirm"
                  className="rounded-sm bg-red-500 text-black hover:bg-red-400 disabled:opacity-40 font-mono tracking-widest text-[10px] px-4 py-2">
            {busy ? "CANCELANDO..." : "CONFIRMAR CANCELAMENTO"}
          </button>
        </div>
      </div>
    </div>
  );
}


function CouponsPanel({ H, setErr }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: "", amount: "5", max_uses: "1", valid_days: "30", note: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/coupons`, { headers: H() });
      if (r.ok) setCoupons((await r.json()).coupons || []);
    } finally { setLoading(false); }
  }, [H]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const cents = Math.round(parseFloat(form.amount || "0") * 100);
    if (!cents || cents < 1) { setErr("Valor inválido"); return; }
    const r = await fetch(`${API}/api/admin/coupons`, {
      method: "POST", headers: H(),
      body: JSON.stringify({
        code: form.code, amount_cents: cents,
        max_uses: parseInt(form.max_uses || "1", 10),
        valid_days: parseInt(form.valid_days || "30", 10),
        note: form.note || null,
      }),
    });
    if (!r.ok) { setErr((await r.json()).detail || "Falha"); return; }
    setForm({ code: "", amount: "5", max_uses: "1", valid_days: "30", note: "" });
    load();
  };

  const deactivate = async (code) => {
    if (!window.confirm(`Desativar cupom ${code}?`)) return;
    await fetch(`${API}/api/admin/coupons/${code}/deactivate`, { method: "POST", headers: H() });
    load();
  };

  return (
    <div className="space-y-6" data-testid="coupons-panel">
      <form onSubmit={submit}
            className="rounded-sm border border-fuchsia-400/40 bg-black/60 p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        <label className="col-span-2">
          <span className="font-mono text-[9px] tracking-widest text-muted-foreground">CÓDIGO</span>
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                 required minLength={3} placeholder="ex: WELCOME2026"
                 data-testid="coupon-code-input"
                 className="w-full bg-black/70 border border-fuchsia-400/30 rounded-sm px-2 py-1.5 font-mono text-xs text-fuchsia-100 uppercase" />
        </label>
        <label>
          <span className="font-mono text-[9px] tracking-widest text-muted-foreground">VALOR (USD)</span>
          <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                 required type="number" step="0.01" min="0.01"
                 data-testid="coupon-amount-input"
                 className="w-full bg-black/70 border border-fuchsia-400/30 rounded-sm px-2 py-1.5 font-mono text-xs text-fuchsia-100" />
        </label>
        <label>
          <span className="font-mono text-[9px] tracking-widest text-muted-foreground">USOS MÁX</span>
          <input value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                 required type="number" min="1"
                 data-testid="coupon-maxuses-input"
                 className="w-full bg-black/70 border border-fuchsia-400/30 rounded-sm px-2 py-1.5 font-mono text-xs text-fuchsia-100" />
        </label>
        <label>
          <span className="font-mono text-[9px] tracking-widest text-muted-foreground">VÁLIDO (DIAS)</span>
          <input value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: e.target.value })}
                 required type="number" min="1"
                 className="w-full bg-black/70 border border-fuchsia-400/30 rounded-sm px-2 py-1.5 font-mono text-xs text-fuchsia-100" />
        </label>
        <button type="submit" data-testid="coupon-create-btn"
                className="rounded-sm bg-fuchsia-500 text-black hover:bg-fuchsia-400 font-mono tracking-widest text-[10px] px-4 py-2">
          CRIAR CUPOM
        </button>
        <label className="col-span-full">
          <span className="font-mono text-[9px] tracking-widest text-muted-foreground">NOTA (opcional)</span>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                 placeholder="ex: Cupom lançamento Twitter"
                 className="w-full bg-black/70 border border-fuchsia-400/30 rounded-sm px-2 py-1.5 font-mono text-xs text-fuchsia-100" />
        </label>
      </form>

      <div>
        <h3 className="font-display font-black text-lg tracking-tighter text-fuchsia-300 mb-2">CUPONS · {coupons.length}</h3>
        {loading ? <p className="font-mono text-xs text-muted-foreground">Carregando...</p> :
         coupons.length === 0 ? (
           <p className="font-mono text-xs text-muted-foreground border border-primary/20 rounded-sm p-6 text-center">
             Nenhum cupom criado ainda. Use o formulário acima pra criar o primeiro código promocional.
           </p>
         ) : (
           <div className="space-y-2" data-testid="coupons-list">
             {coupons.map((c) => (
               <div key={c.code} data-testid={`coupon-row-${c.code}`}
                    className={`rounded-sm border p-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-center font-mono text-xs
                      ${c.is_active ? "border-fuchsia-400/30 bg-fuchsia-500/5" : "border-muted/30 bg-black/30 opacity-60"}`}>
                 <span className="text-fuchsia-200 font-bold">{c.code}</span>
                 <span className="text-emerald-300">${(c.amount_cents / 100).toFixed(2)}</span>
                 <span className="text-white">{c.uses_count}/{c.max_uses} usos</span>
                 <span className="text-muted-foreground">exp {fmtDate(c.expires_at)}</span>
                 <span className="text-muted-foreground italic truncate" title={c.note}>{c.note || "—"}</span>
                 <div className="flex justify-end">
                   {c.is_active && (
                     <button onClick={() => deactivate(c.code)}
                             data-testid={`coupon-deactivate-${c.code}`}
                             className="text-[10px] tracking-widest text-red-300 hover:bg-red-500/10 px-2 py-1 rounded-sm border border-red-400/40">
                       DESATIVAR
                     </button>
                   )}
                   {!c.is_active && <span className="text-[9px] tracking-widest text-muted-foreground">DESATIVADO</span>}
                 </div>
               </div>
             ))}
           </div>
         )}
      </div>
    </div>
  );
}


function PayoutsPanel({ house, stats, pending, paid, cancelled, onMarkPaid, onCancel, onHouseWithdraw }) {
  const [payTarget, setPayTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [showHouseWithdraw, setShowHouseWithdraw] = useState(false);
  const [historyTab, setHistoryTab] = useState("paid");
  const [historyFilter, setHistoryFilter] = useState("");
  const filterFn = (p) => {
    if (!historyFilter.trim()) return true;
    const q = historyFilter.toLowerCase();
    return (p.display_name || "").toLowerCase().includes(q)
        || (p.usdt_wallet_address || "").toLowerCase().includes(q)
        || (p.tx_hash || "").toLowerCase().includes(q);
  };
  const paidF = paid.filter(filterFn);
  const cancelledF = cancelled.filter(filterFn);
  return (
    <div className="space-y-6" data-testid="admin-payouts-panel">
      {/* Cofre da Plataforma */}
      {house && (
        <div className="rounded-sm border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent p-6 space-y-4" data-testid="house-wallet-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Vault className="h-10 w-10 text-emerald-300" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-emerald-300">// COFRE DA PLATAFORMA</p>
                <h2 className="font-display font-black text-3xl tracking-tighter text-emerald-200">
                  {fmtUsd(house.balance_cents)} <span className="text-emerald-400 text-lg">USDT</span>
                </h2>
              </div>
            </div>
            <button
              onClick={() => setShowHouseWithdraw(true)}
              data-testid="house-withdraw-btn"
              disabled={house.balance_cents <= 0}
              className="inline-flex items-center gap-2 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed font-mono tracking-widest text-[11px] px-4 py-2.5 transition"
            >
              <ArrowUpCircle className="h-3.5 w-3.5" /> SACAR CASA
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-sm border border-emerald-400/30 bg-black/40 p-3">
              <p className="font-mono text-[9px] tracking-widest text-emerald-300/70">GANHOS ÚLTIMOS 7 DIAS</p>
              <p className="font-display font-black text-xl tracking-tighter text-emerald-300" data-testid="house-gain-7d">
                <TrendingUp className="inline h-4 w-4 mr-1" />+{fmtUsd(house.gain_7d_cents)}
              </p>
            </div>
            <div className="rounded-sm border border-emerald-400/30 bg-black/40 p-3">
              <p className="font-mono text-[9px] tracking-widest text-emerald-300/70">GANHOS ÚLTIMOS 30 DIAS</p>
              <p className="font-display font-black text-xl tracking-tighter text-emerald-300" data-testid="house-gain-30d">
                <TrendingUp className="inline h-4 w-4 mr-1" />+{fmtUsd(house.gain_30d_cents)}
              </p>
            </div>
          </div>
          {house.recent_entries && house.recent_entries.length > 0 && (
            <div className="space-y-1">
              <p className="font-mono text-[9px] tracking-widest text-muted-foreground">HISTÓRICO RECENTE ({house.recent_entries.length})</p>
              <div className="max-h-52 overflow-y-auto space-y-1 pr-1" data-testid="house-recent-entries">
                {house.recent_entries.map((e) => {
                  const isCredit = e.delta_cents > 0;
                  return (
                    <div key={e.entry_id} className="grid grid-cols-[auto_1fr_auto] gap-2 items-center rounded-sm border border-emerald-400/15 bg-black/40 px-3 py-2 font-mono text-[10px]">
                      <span className={`tracking-widest ${isCredit ? "text-emerald-300" : "text-orange-300"}`}>
                        {e.reason === "platform_fee" ? "TAXA" : e.reason === "house_withdraw" ? "SAQUE" : e.reason.toUpperCase()}
                      </span>
                      <span className="text-white truncate">
                        {e.room_label ? `${e.room_label} · entry ${fmtUsd(e.room_entry_fee_cents)}` : e.reason}
                        <span className="text-muted-foreground ml-2">{fmtDate(e.created_at)}</span>
                      </span>
                      <span className={`text-right font-bold ${isCredit ? "text-emerald-300" : "text-orange-300"}`}>
                        {isCredit ? "+" : ""}{fmtUsd(e.delta_cents)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-sm border border-amber-300/40 bg-black/70 p-4">
            <p className="font-mono text-[9px] tracking-widest text-amber-200/70">SAQUES PENDENTES</p>
            <p className="font-display font-black text-3xl tracking-tighter text-amber-200" data-testid="stat-pending-count">{stats.pending_payouts}</p>
          </div>
          <div className="rounded-sm border border-emerald-500/40 bg-black/70 p-4">
            <p className="font-mono text-[9px] tracking-widest text-emerald-300/70">HOUSE TAKE (JANELA)</p>
            <p className="font-display font-black text-3xl tracking-tighter text-emerald-300" data-testid="stat-house-take">{fmtUsd(stats.house_take_cents)}</p>
          </div>
          <div className="rounded-sm border border-primary/40 bg-black/70 p-4">
            <p className="font-mono text-[9px] tracking-widest text-primary/70">SALAS ATIVAS</p>
            <p className="font-display font-black text-3xl tracking-tighter text-primary">{stats.active_rooms}</p>
          </div>
          <div className="rounded-sm border border-secondary/40 bg-black/70 p-4">
            <p className="font-mono text-[9px] tracking-widest text-secondary/70">FECHADAS · CANCELADAS</p>
            <p className="font-display font-black text-3xl tracking-tighter text-secondary">{stats.closed_rooms} · {stats.cancelled_rooms}</p>
          </div>
        </div>
      )}

      {/* Pending payouts */}
      <div className="space-y-3">
        <h2 className="font-display font-black text-xl tracking-tighter text-amber-200">SAQUES PARA PROCESSAR · {pending.length}</h2>
        <p className="font-mono text-[10px] tracking-widest text-muted-foreground">
          Estes são saques SOLICITADOS pelos jogadores. Envie o USDT para o endereço e marque como pago com o tx_hash.
        </p>
        {pending.length === 0 ? (
          <p className="font-mono text-xs text-muted-foreground border border-primary/20 rounded-sm p-6 text-center">Nada pendente. Boa!</p>
        ) : (
          <div className="space-y-2">
            {pending.map((p) => (
              <div key={p.payout_id} data-testid={`pending-${p.payout_id}`} className="rounded-sm border border-amber-300/40 bg-black/70 p-4 grid grid-cols-1 md:grid-cols-7 gap-3 items-center">
                <div>
                  <p className="font-mono text-[9px] text-muted-foreground">PILOTO</p>
                  <p className="font-mono text-sm text-white">
                    {p.display_name}
                    {p.payout_type === "withdraw" ? (
                      <span className="ml-2 text-[9px] tracking-widest text-amber-200 border border-amber-300/40 px-1.5 py-[1px] rounded-sm">SAQUE</span>
                    ) : p.rank ? (
                      <span className="text-amber-200"> {p.rank}º</span>
                    ) : null}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[9px] text-muted-foreground">VALOR</p>
                  <p className="font-display font-black text-xl text-emerald-300">{fmtUsd(p.amount_cents)}</p>
                  {p.fee_cents ? (
                    <p className="font-mono text-[9px] text-muted-foreground">taxa {fmtUsd(p.fee_cents)}</p>
                  ) : null}
                </div>
                <div>
                  <p className="font-mono text-[9px] text-muted-foreground">REDE</p>
                  <p className="font-mono text-xs text-primary">{p.usdt_network}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="font-mono text-[9px] text-muted-foreground">ENDEREÇO USDT</p>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-primary break-all">{p.usdt_wallet_address}</span>
                    <CopyBtn text={p.usdt_wallet_address} testid={`copy-${p.payout_id}`} />
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[9px] text-muted-foreground">DATA</p>
                  <p className="font-mono text-[10px] text-white">{fmtDate(p.created_at)}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => setPayTarget(p)} data-testid={`mark-paid-${p.payout_id}`}
                    className="w-full inline-flex items-center justify-center gap-1 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 font-mono tracking-widest text-[10px] px-3 py-2">
                    <CheckCircle2 className="h-3 w-3" /> CONFIRMAR
                  </button>
                  {p.payout_type === "withdraw" && (
                    <button onClick={() => setCancelTarget(p)} data-testid={`cancel-${p.payout_id}`}
                      className="w-full inline-flex items-center justify-center gap-1 rounded-sm border border-red-400/50 text-red-300 hover:bg-red-500/10 font-mono tracking-widest text-[10px] px-3 py-2">
                      <XCircle className="h-3 w-3" /> CANCELAR
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Paid + Cancelled history — segmented control + search filter */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 border-b border-primary/20 pb-2">
          <button
            onClick={() => setHistoryTab("paid")}
            data-testid="history-tab-paid"
            className={`font-mono tracking-widest text-[10px] px-3 py-1.5 rounded-sm border ${
              historyTab === "paid"
                ? "border-emerald-400 text-emerald-300 bg-emerald-500/10"
                : "border-transparent text-muted-foreground hover:text-white"
            }`}
          >
            PAGOS · {paidF.length}
          </button>
          <button
            onClick={() => setHistoryTab("cancelled")}
            data-testid="history-tab-cancelled"
            className={`font-mono tracking-widest text-[10px] px-3 py-1.5 rounded-sm border ${
              historyTab === "cancelled"
                ? "border-red-400 text-red-300 bg-red-500/10"
                : "border-transparent text-muted-foreground hover:text-white"
            }`}
          >
            CANCELADOS · {cancelledF.length}
          </button>
          <div className="ml-auto flex items-center gap-1 min-w-[220px]">
            <input
              value={historyFilter}
              onChange={(e) => setHistoryFilter(e.target.value)}
              placeholder="Buscar piloto, endereço, tx_hash..."
              data-testid="history-filter-input"
              className="w-full bg-black/60 border border-primary/30 rounded-sm px-2 py-1 font-mono text-[10px] text-primary placeholder-muted-foreground/60"
            />
            {historyFilter && (
              <button
                onClick={() => setHistoryFilter("")}
                data-testid="history-filter-clear"
                className="text-muted-foreground hover:text-white p-1"
                aria-label="Clear filter"
              >
                <XCircle className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {historyTab === "paid" && (
          paidF.length === 0 ? (
            <p className="font-mono text-xs text-muted-foreground border border-primary/20 rounded-sm p-6 text-center">
              {historyFilter ? `Nenhum resultado pra "${historyFilter}".` : "Nenhum payout enviado ainda."}
            </p>
          ) : (
            <div className="space-y-2" data-testid="history-list-paid">
              {paidF.map((p) => (
                <div key={p.payout_id} className="rounded-sm border border-primary/20 bg-black/50 p-3 grid grid-cols-1 md:grid-cols-5 gap-2 font-mono text-xs items-center">
                  <span className="text-white">
                    {p.display_name}
                    {p.payout_type === "withdraw" ? (
                      <span className="ml-2 text-[9px] tracking-widest text-amber-200/70">SAQUE</span>
                    ) : p.rank ? (
                      <span className="text-secondary"> {p.rank}º</span>
                    ) : null}
                  </span>
                  <span className="text-emerald-300 font-bold">{fmtUsd(p.amount_cents)} {p.usdt_network}</span>
                  <span className="text-primary text-[10px] break-all">{p.usdt_wallet_address?.slice(0, 12)}...</span>
                  <span className="text-muted-foreground text-[10px] break-all">tx: {p.tx_hash?.slice(0, 16)}...</span>
                  <span className="text-muted-foreground text-[10px]">{fmtDate(p.paid_at)}</span>
                </div>
              ))}
            </div>
          )
        )}

        {historyTab === "cancelled" && (
          cancelledF.length === 0 ? (
            <p className="font-mono text-xs text-muted-foreground border border-primary/20 rounded-sm p-6 text-center">
              {historyFilter ? `Nenhum resultado pra "${historyFilter}".` : "Nenhum saque cancelado. Quando você cancelar um pedido, ele aparece aqui com o motivo e o valor foi devolvido pra Prize Wallet do piloto."}
            </p>
          ) : (
            <div className="space-y-2" data-testid="history-list-cancelled">
              {cancelledF.map((p) => (
                <div key={p.payout_id} data-testid={`cancelled-${p.payout_id}`}
                     className="rounded-sm border border-red-400/20 bg-red-500/5 p-3 grid grid-cols-1 md:grid-cols-5 gap-2 font-mono text-xs items-center">
                  <span className="text-white">
                    {p.display_name}
                    <span className="ml-2 text-[9px] tracking-widest text-red-300/80">CANCELADO</span>
                  </span>
                  <span className="text-red-300 font-bold">{fmtUsd(p.gross_cents || p.amount_cents)} devolvido</span>
                  <span className="text-primary text-[10px] break-all">{p.usdt_wallet_address?.slice(0, 12)}...</span>
                  <span className="text-muted-foreground text-[10px] italic truncate" title={p.cancel_reason}>
                    "{p.cancel_reason || "sem motivo"}"
                  </span>
                  <span className="text-muted-foreground text-[10px]">{fmtDate(p.cancelled_at)}</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <PayModalInner payout={payTarget} onClose={() => setPayTarget(null)}
        onSubmit={async (tx, note) => {
          const ok = await onMarkPaid(payTarget.payout_id, tx, note);
          if (ok) setPayTarget(null);
        }} />
      <CancelPayoutModal payout={cancelTarget} onClose={() => setCancelTarget(null)}
        onSubmit={async (reason) => {
          const ok = await onCancel(cancelTarget.payout_id, reason);
          if (ok) setCancelTarget(null);
        }} />
      <HouseWithdrawModal open={showHouseWithdraw} balanceCents={house?.balance_cents ?? 0}
        onClose={() => setShowHouseWithdraw(false)}
        onSubmit={async (amountCents, note) => {
          const ok = await onHouseWithdraw(amountCents, note);
          if (ok) setShowHouseWithdraw(false);
        }} />
    </div>
  );
}

function CopyBtn({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1400); }
        catch (err) { console.warn("copy failed:", err); }
      }}
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-sm border border-primary/40 hover:bg-primary/10 text-primary font-mono text-[9px] tracking-widest px-2 py-1"
    >
      {copied ? <><CheckCircle2 className="h-3 w-3" /> COPIADO</> : <><Copy className="h-3 w-3" /> COPIAR</>}
    </button>
  );
}

function PayModalInner({ payout, onClose, onSubmit }) {
  const [tx, setTx] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!payout) return null;
  const submit = async () => {
    if (tx.trim().length < 6) { setErr("tx_hash inválido"); return; }
    setBusy(true); setErr(null);
    try { await onSubmit(tx.trim(), note); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="pay-modal">
      <div className="w-full max-w-md rounded-sm border border-emerald-500/50 bg-gradient-to-b from-[#050a18] to-[#020408] p-6 space-y-4">
        <h2 className="font-display font-black text-xl tracking-tighter text-emerald-300">MARCAR COMO PAGO</h2>
        <div className="text-xs font-mono text-muted-foreground space-y-1 border border-primary/20 rounded-sm p-3">
          <p><span className="text-muted-foreground">Piloto:</span> <span className="text-white">{payout.display_name}</span> {payout.payout_type === "withdraw" ? <span className="text-amber-200">(SAQUE SOLICITADO)</span> : payout.rank ? <span>({payout.rank}º lugar)</span> : null}</p>
          <p><span className="text-muted-foreground">Valor:</span> <span className="text-emerald-300 font-bold">{fmtUsd(payout.amount_cents)} USDT ({payout.usdt_network})</span></p>
          <p className="break-all"><span className="text-muted-foreground">Endereço:</span> <span className="text-primary">{payout.usdt_wallet_address}</span></p>
        </div>
        <input value={tx} onChange={(e) => setTx(e.target.value)} placeholder="Cole aqui o tx_hash da transação USDT"
          data-testid="pay-tx-hash"
          className="w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-xs text-primary placeholder-muted-foreground/60" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota interna (opcional)"
          data-testid="pay-note"
          className="w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-xs text-primary placeholder-muted-foreground/60" />
        {err && <div className="rounded-sm border border-red-500/50 bg-red-500/10 p-2 text-xs text-red-300 font-mono">{err}</div>}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} data-testid="pay-cancel"
            className="rounded-sm border border-primary/30 hover:bg-primary/10 text-primary font-mono tracking-widest text-[10px] px-3 py-3">CANCELAR</button>
          <button onClick={submit} disabled={busy} data-testid="pay-submit"
            className="inline-flex items-center justify-center gap-2 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 font-mono tracking-widest text-[10px] px-3 py-3 disabled:opacity-40">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5" /> CONFIRMAR</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function HouseWithdrawModal({ open, balanceCents, onClose, onSubmit }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!open) return null;
  const amountCents = Math.round(parseFloat(amount || "0") * 100);
  const valid = amountCents > 0 && amountCents <= (balanceCents ?? 0);
  const submit = async () => {
    setBusy(true); setErr(null);
    try { await onSubmit(amountCents, note); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="house-withdraw-modal">
      <div className="w-full max-w-md rounded-sm border border-emerald-500/50 bg-gradient-to-b from-[#050a18] to-[#020408] p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Vault className="h-6 w-6 text-emerald-300" />
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-emerald-300">// COFRE → ADMIN</p>
            <h2 className="font-display font-black text-xl tracking-tighter">DEBITAR CASA</h2>
          </div>
        </div>
        <div className="rounded-sm border border-primary/20 bg-black/40 p-3">
          <p className="font-mono text-[9px] tracking-widest text-muted-foreground">SALDO DO COFRE</p>
          <p className="font-display font-black text-2xl tracking-tighter text-emerald-300">{fmtUsd(balanceCents)}</p>
        </div>
        <p className="text-xs font-mono text-muted-foreground leading-relaxed">
          Registra que você já moveu esse USDT para sua carteira pessoal. Bookkeeping apenas — não faz transação on-chain.
        </p>
        <div className="flex gap-2">
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
            data-testid="house-withdraw-amount"
            className="w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-base text-primary placeholder-muted-foreground/60" />
          <button type="button" onClick={() => setAmount((balanceCents / 100).toFixed(2))}
            data-testid="house-withdraw-max"
            className="rounded-sm border border-primary/30 hover:bg-primary/10 text-primary font-mono text-[10px] tracking-widest px-3 whitespace-nowrap">MÁX</button>
        </div>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota interna (opcional)"
          data-testid="house-withdraw-note"
          className="w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-xs text-primary placeholder-muted-foreground/60" />
        {err && <div className="rounded-sm border border-red-500/50 bg-red-500/10 p-2 text-xs text-red-300 font-mono">{err}</div>}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} data-testid="house-withdraw-cancel"
            className="rounded-sm border border-primary/30 hover:bg-primary/10 text-primary font-mono tracking-widest text-[10px] px-3 py-3">CANCELAR</button>
          <button onClick={submit} disabled={busy || !valid} data-testid="house-withdraw-confirm"
            className="inline-flex items-center justify-center gap-2 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40 font-mono tracking-widest text-[10px] px-3 py-3">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5" /> CONFIRMAR</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, testid, hint }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-widest text-muted-foreground">{label}</span>
      <input type="text" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-sm" />
      {hint && <span className="font-mono text-[9px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function NumberField({ label, value, onChange, testid, hint }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-widest text-muted-foreground">{label}</span>
      <input type="number" data-testid={testid} value={value} onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        className="mt-1 w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-sm" />
      {hint && <span className="font-mono text-[9px] text-muted-foreground">{hint}</span>}
    </label>
  );
}
